import React, { useState, useRef, useLayoutEffect, useMemo } from "react";
import { rolesOf, roleLabel } from "../../lib/roles";
import { NavLink, Link, Outlet, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import NotificationBell from "../../components/NotificationBell";
import { LOGO_URL } from "../../lib/constants";
import {
  LayoutDashboard, Users, BadgeIndianRupee, Package, FileText, Heart, Hammer,
  Sparkles, Calendar, ListChecks, LogOut, ScrollText, ShoppingBag, Home, Menu, X, Palette, Gift } from "lucide-react";

const MENU = [
  { to: "/staff", label: "Dashboard", icon: LayoutDashboard, roles: "*", end: true },
  { to: "/staff/revenue", label: "Revenue Details", icon: BadgeIndianRupee, roles: ["super_admin", "major_admin", "admin"] },
  { to: "/staff/lock-rates", label: "Rate-Lock Tokens", icon: BadgeIndianRupee, roles: ["super_admin", "major_admin", "admin"] },
  { to: "/staff/stores", label: "Stores", icon: Package, roles: ["super_admin"] },
  { to: "/staff/dashboard-period", label: "Sales Insights", icon: LayoutDashboard, roles: ["super_admin", "major_admin", "admin"] },
  { to: "/staff/users", label: "Users", icon: Users, roles: ["super_admin", "major_admin"] },
  { to: "/staff/rates", label: "Daily Rates", icon: BadgeIndianRupee, roles: ["super_admin", "major_admin", "admin"] },
  { to: "/staff/rates/history", label: "Rate Audit Log", icon: ScrollText, roles: ["super_admin", "major_admin"] },
  { to: "/staff/audit-log", label: "Audit Log", icon: ScrollText, roles: ["super_admin", "major_admin"] },
  { to: "/staff/products", label: "Products / Stock", icon: Package, roles: ["super_admin", "major_admin", "catalogue_admin", "admin"], anyFeature: ["products_edit"] },
  { to: "/staff/inventory", label: "Inventory Ledger", icon: Package, roles: ["super_admin", "major_admin", "admin", "catalogue_admin"], anyFeature: ["products_edit"] },
  { to: "/staff/stock-by-store", label: "Stock by Store", icon: Package, roles: ["super_admin", "major_admin", "admin", "catalogue_admin"], anyFeature: ["products_edit"] },
  { to: "/staff/permissions", label: "Feature Toggles", icon: Users, roles: ["super_admin", "major_admin"] },
  { to: "/staff/workflow", label: "Workflow Checklist", icon: ListChecks, roles: ["salesperson", "admin", "major_admin", "super_admin"] },
  { to: "/staff/scheme-reminders", label: "Scheme Reminders", icon: Sparkles, roles: ["super_admin", "major_admin", "admin"] },
  { to: "/staff/click-pack", label: "EOD Click-Pack", icon: Heart, roles: ["salesperson", "admin", "major_admin", "super_admin"] },
  { to: "/staff/festival-engine", label: "Festival Engine", icon: Calendar, roles: ["super_admin", "major_admin", "admin"] },
  { to: "/staff/blogs", label: "Blogs", icon: FileText, roles: ["super_admin", "major_admin", "admin"] },
  { to: "/staff/site-cms", label: "Site CMS", icon: FileText, roles: ["super_admin", "major_admin", "admin"] },
  // Super Admin owns this; a Major Admin/Admin sees it only once the Super Admin
  // delegates "home_video" or "our_story" from Feature Toggles.
  { to: "/staff/home-story", label: "Home & Story Content", icon: FileText, roles: ["super_admin", "major_admin", "admin"], anyFeature: ["home_video", "our_story", "home_scheme"] },
  { to: "/staff/customers", label: "Customers", icon: Heart, roles: ["salesperson", "admin", "major_admin", "super_admin", "billing_desk"] },
  { to: "/staff/orders", label: "Orders", icon: ShoppingBag, roles: ["salesperson", "admin", "major_admin", "super_admin"] },
  { to: "/staff/slips", label: "Slip to Bill", icon: ScrollText, roles: ["salesperson", "admin", "major_admin", "super_admin", "billing_desk"] },
  { to: "/staff/customisations", label: "Customisations", icon: Palette, roles: ["salesperson", "admin", "major_admin", "super_admin"] },
  { to: "/staff/sales-by-person", label: "Sales by Person", icon: BadgeIndianRupee, roles: ["salesperson", "admin", "major_admin", "super_admin"] },
  { to: "/staff/sales-analytics", label: "Sales Analytics", icon: BadgeIndianRupee, roles: ["salesperson", "admin", "major_admin", "super_admin"] },
  { to: "/staff/karigar", label: "Karigar Workflow", icon: Hammer, roles: ["salesperson", "admin", "major_admin", "super_admin", "karigar"] },
  { to: "/staff/payouts", label: "Payouts", icon: BadgeIndianRupee, roles: ["super_admin", "major_admin"] },
  { to: "/staff/schemes", label: "Schemes", icon: Sparkles, roles: ["salesperson", "admin", "major_admin", "super_admin"] },
  { to: "/staff/personal-catalog", label: "Personal Catalog", icon: ListChecks, roles: ["salesperson", "admin", "major_admin", "super_admin"] },
  { to: "/staff/attendance", label: "My Attendance", icon: Calendar, roles: ["salesperson", "karigar", "astrologer", "admin", "billing_desk"] },
  { to: "/staff/billing", label: "Billing Desk", icon: BadgeIndianRupee, roles: ["super_admin", "major_admin", "admin", "billing_desk"] },
  { to: "/staff/inventory", label: "Live Inventory", icon: Package, roles: ["super_admin", "major_admin", "admin"] },
  { to: "/staff/sold-products", label: "Sold Products", icon: ShoppingBag, roles: ["super_admin", "major_admin", "admin"] },
  { to: "/staff/gold-scheme", label: "Gold Scheme", icon: BadgeIndianRupee, roles: ["super_admin", "major_admin", "admin", "salesperson"] },
  { to: "/staff/customisation-designs", label: "Customisation Designs", icon: Palette, roles: ["super_admin", "major_admin", "admin"] },
  { to: "/staff/referrals", label: "Referrals", icon: Gift, roles: ["super_admin", "major_admin"] },
  { to: "/staff/attendance-records", label: "Attendance Records", icon: Calendar, roles: ["super_admin", "major_admin"] },
  { to: "/staff/targets", label: "Sales Targets", icon: ScrollText, roles: ["super_admin", "major_admin", "admin", "salesperson"] },
  { to: "/staff/eod", label: "EOD Report", icon: ScrollText, roles: ["salesperson", "admin"] },
  { to: "/staff/leave", label: "Leave Application", icon: Calendar, roles: ["salesperson"] },
  { to: "/staff/leave-approvals", label: "Leave Applications", icon: ListChecks, roles: ["super_admin", "major_admin", "admin"] },
  { to: "/staff/coupons", label: "Coupons", icon: Sparkles, roles: ["super_admin", "major_admin", "admin"] },
  { to: "/staff/wa-templates", label: "WhatsApp Templates", icon: FileText, roles: ["super_admin", "major_admin", "admin"] },
  { to: "/staff/festival", label: "Festival CRM", icon: Heart, roles: ["super_admin", "major_admin", "admin"] },
  { to: "/staff/predictive", label: "Predictive CRM", icon: Heart, roles: ["super_admin", "major_admin", "admin"] },
  { to: "/staff/migration", label: "Data Migration", icon: FileText, roles: ["super_admin", "major_admin"] },
  { to: "/staff/astrology", label: "Astrologer Inbox", icon: Calendar, roles: ["astrologer", "super_admin"] },
];


/**
 * Sidebar links.
 *
 * Defined at module scope on purpose. When this lived inside StaffLayout it was
 * a NEW component type on every render, so React threw away the <nav> subtree
 * and rebuilt it — which reset the menu's scroll to the top every time you
 * clicked an item. Keeping it stable here preserves the scroll position.
 */
const NavItems = React.memo(function NavItems({ items, onNavigate }) {
  return (
    <>
      <Link
        to="/"
        onClick={onNavigate}
        data-testid="menu-go-home"
        className="flex items-center gap-3 px-3 py-2.5 text-sm rounded bg-gold/15 text-maroon border border-gold mb-2 hover:bg-gold/25"
      >
        <Home size={16}/> Go to Homepage
      </Link>
      {items.map((m) => (
        <NavLink
          key={m.to} to={m.to} end={m.end}
          onClick={onNavigate}
          data-testid={`menu-${m.label.toLowerCase().replace(/\s+/g, "-")}`}
          className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 text-sm rounded ${isActive ? "bg-maroon text-pearl" : "text-ink hover:bg-pearl"}`}>
          <m.icon size={16}/> {m.label}
        </NavLink>
      ))}
    </>
  );
});

export default function StaffLayout() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  // The sidebar has its own scroll. Clicking a menu item re-renders the layout,
  // which was snapping the menu back to the top — so remember where the user
  // was and put it back after navigating.
  const mainRef = useRef(null);
  const navScrollRef = useRef(null);
  const navScrollPos = useRef(0);
  useLayoutEffect(() => {
    const el = navScrollRef.current;
    if (el && navScrollPos.current) el.scrollTop = navScrollPos.current;
    // Show the new section from its start, instantly — no smooth animation
    // yanking the page, and the sidebar keeps its own position.
    if (mainRef.current) mainRef.current.scrollTop = 0;
  }, [location.pathname]);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close the drawer automatically whenever the route changes (link tapped,
  // back button, etc.) so it never stays stuck open after navigating.
  React.useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // NOTE: this must sit ABOVE the early returns below — hooks have to run in
  // the same order on every render, so it cannot live after a conditional
  // return. Memoised so <NavItems> gets a stable prop and React.memo can skip
  // the re-render, which is what keeps the menu's scroll position on navigation.
  const menuItems = useMemo(() => {
    if (!user) return [];
    const granted = new Set(user.features || []);
    // A person can hold several roles; they see the UNION of every role's menu.
    const roles = rolesOf(user);
    const others = roles.filter((r) => r !== "billing_desk");
    // Billing desk sees billing, slip-to-bill, add-customer and attendance.
    const billingAllow = ["/staff/billing", "/staff/slips", "/staff/customers", "/staff/attendance"];
    if (roles.includes("billing_desk") && others.length === 0) {
      return MENU.filter((m) => billingAllow.includes(m.to));   // billing desk ONLY
    }
    return MENU.filter((m) => {
      if (roles.includes("billing_desk") && billingAllow.includes(m.to)) return true;
      if (m.roles === "*" || m.roles.some((r) => others.includes(r))) return true;
      // Delegated access: Super Admin can grant individual tasks to other staff
      if (m.anyFeature && (granted.has("*") || m.anyFeature.some((f) => granted.has(f)))) return true;
      return false;
    });
  }, [user]);

  if (loading) return <div className="p-10">Loading…</div>;
  if (!user) return <Navigate to="/staff/login" replace />;
  if (user.role === "customer") return <Navigate to="/" replace />;

  const onLogout = () => { logout(); nav("/staff/login"); };

  return (
    <div className="h-screen overflow-hidden bg-pearl flex flex-col lg:flex-row">
      {/* ===== Mobile / Tablet top bar (below lg) — this was completely
          missing before, so staff on phones/tablets had no way to reach
          any dashboard section at all. ===== */}
      <header className="lg:hidden shrink-0 z-40 bg-white border-b border-line flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2">
          <img src={LOGO_URL} alt="" className="h-8"/>
          <div>
            <div className="font-display text-base text-maroon leading-none">Swayambhu</div>
            <div className="eyebrow text-[7px]">CRM Console</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
        <NotificationBell />
        <button
          onClick={() => setMobileOpen(true)}
          data-testid="mobile-menu-open"
          aria-label="Open menu"
          className="p-2 border border-line rounded text-maroon"
        >
          <Menu size={20}/>
        </button>
        </div>
      </header>

      {/* ===== Mobile / Tablet slide-in drawer ===== */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)}/>
          <aside className="relative w-[82%] max-w-xs bg-white h-full flex flex-col shadow-xl">
            <div className="p-4 border-b border-line flex items-center justify-between">
              <div className="flex items-center gap-2">
                <img src={LOGO_URL} alt="" className="h-8"/>
                <div>
                  <div className="font-display text-base text-maroon leading-none">Swayambhu</div>
                  <div className="eyebrow text-[7px]">CRM Console</div>
                </div>
              </div>
              <button onClick={() => setMobileOpen(false)} data-testid="mobile-menu-close" aria-label="Close menu">
                <X size={20}/>
              </button>
            </div>
            <nav className="dash-pane flex-1 min-h-0 p-3 overflow-y-auto">
              <NavItems items={menuItems} onNavigate={() => setMobileOpen(false)}/>
            </nav>
            <div className="p-3 border-t border-line">
              <div className="text-xs">{user.name}</div>
              <div className="eyebrow text-[9px] text-ash" data-testid="user-role-label">{roleLabel(user)}</div>
              <button onClick={onLogout} className="mt-2 flex items-center gap-2 text-xs text-maroon hover:underline" data-testid="staff-logout-mobile">
                <LogOut size={12}/> Logout
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* ===== Desktop sidebar (lg and up) — unchanged ===== */}
      <aside className="hidden lg:flex w-64 bg-white border-r border-line flex-col h-full shrink-0">
        <div className="p-5 border-b border-line">
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="" className="h-9"/>
            <div>
              <div className="font-display text-lg text-maroon leading-none">Swayambhu</div>
              <div className="eyebrow text-[8px]">CRM Console</div>
            </div>
          </div>
        </div>
        <nav
          ref={navScrollRef}
          onScroll={(e) => { navScrollPos.current = e.currentTarget.scrollTop; }}
          className="dash-pane flex-1 min-h-0 p-3 overflow-y-auto"
        >
          <NavItems items={menuItems}/>
        </nav>
        <div className="p-3 border-t border-line">
          <div className="text-xs">{user.name}</div>
          <div className="eyebrow text-[9px] text-ash" data-testid="user-role-label">{roleLabel(user)}</div>
          <button onClick={onLogout} className="mt-2 flex items-center gap-2 text-xs text-maroon hover:underline" data-testid="staff-logout">
            <LogOut size={12}/> Logout
          </button>
        </div>
      </aside>

      <main ref={mainRef} className="dash-pane flex-1 min-w-0 min-h-0 overflow-y-auto overflow-x-hidden">
        {/* Desktop top bar — the bell lives here so it is present on every page */}
        <div className="hidden lg:flex items-center justify-end gap-2 px-8 py-2 border-b border-line bg-white/80 backdrop-blur sticky top-0 z-30">
          <span className="text-xs text-ash">{user?.name}</span>
          <NotificationBell />
        </div>
        <Outlet />
      </main>
    </div>
  );
}
